import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  ShieldCheck,
  Clock,
  Utensils,
  ChefHat,
  Calendar,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1768385592801-94eb1d78860f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwZ29sZCUyMGxpZ2h0fGVufDB8fHx8MTc3NzUyMDM1NHww&ixlib=rb-4.1.0&q=85";

// Illustrative restaurant-owner scenarios. Not real customers.
const SCENARIOS = [
  {
    name: "Marcus",
    role: "Restaurant Owner",
    scenario:
      "Marcus wanted to upgrade his kitchen equipment before a busy season and needed to explore funding options that wouldn't tie up his day-to-day cash flow.",
  },
  {
    name: "Maria",
    role: "Café Owner",
    scenario:
      "Maria needed extra cash flow support during a slower month and wanted to explore inventory funding options without going through a traditional bank.",
  },
  {
    name: "Jordan",
    role: "Food Truck Owner",
    scenario:
      "Jordan was preparing to expand to a second truck and wanted to understand the funding options available for growing his mobile business.",
  },
  {
    name: "Devon",
    role: "Bar Owner",
    scenario:
      "Devon was planning a renovation to refresh the dining area and wanted to see what funding options might fit a project of that size.",
  },
  {
    name: "Priya",
    role: "Catering Business Owner",
    scenario:
      "Priya needed working capital to cover payroll between large catering contracts and wanted to explore options outside of a traditional bank loan.",
  },
];

const scrollToForm = () => {
  const el = document.getElementById("apply");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Landing() {
  return (
    <div className="relative min-h-screen bg-[#050505] text-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden hero-radial">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "screen",
          }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />

        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-12 md:px-12 md:py-24 lg:py-32">
          <div className="md:col-span-7">
            <div
              data-testid="hero-overline"
              className="fade-up inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D4AF37] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D4AF37]" />
              </span>
              Funding Available Today
            </div>

            <h1
              data-testid="hero-headline"
              className="fade-up delay-1 mt-6 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            >
              <span className="gold-text-gradient">Restaurant Funding</span> Options —
              <br className="hidden sm:block" />
              <span className="text-zinc-300">See What You May Qualify For in</span>{" "}
              <span className="text-[#D4AF37]">60 Seconds</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="fade-up delay-2 mt-6 max-w-xl text-base text-zinc-300 sm:text-lg"
            >
              Working capital, equipment financing, inventory purchases, restaurant renovations, and expansion funding—explore options from our funding partners without dealing with traditional banks.
            </p>

            <div className="fade-up delay-3 mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                onClick={scrollToForm}
                data-testid="hero-cta-button"
                className="h-14 rounded-lg bg-[#D4AF37] px-8 text-base font-bold text-black hover:bg-[#F0C94F] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300"
              >
                Check My Options →
              </Button>
              <div className="flex items-center gap-2 px-2 text-sm text-zinc-400">
                <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
                Restaurant Funding Specialists
              </div>
            </div>
            
          </div>

          <div className="md:col-span-5">
            <ScenarioCarousel />
          </div>
        </div>
      </section>
{/* TRUST / FIT SECTION */}
<section className="border-t border-white/5 bg-[#070707] px-5 py-10 md:px-12">
  <div className="mx-auto max-w-7xl">
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
        Designed for food businesses
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-zinc-300 sm:grid-cols-5">
        <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="Restaurants" />
        <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="Cafés" />
        <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="Food Trucks" />
        <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="Bars & Taverns" />
        <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="Catering" />
      </div>

      <p className="mt-5 text-sm text-zinc-500">
        Questions? Contact us at{" "}
        <a
          href="mailto:funding@meltedportstudios.com"
          className="text-[#D4AF37] hover:underline"
        >
          funding@meltedportstudios.com
        </a>
      </p>
    </div>
  </div>
</section>
      {/* RESTAURANT EXPENSES BLOCK */}
      <section className="border-t border-white/5 px-5 py-20 md:px-12 md:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
            Built for restaurants
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
            Running a restaurant is expensive—we get it.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-zinc-400">
            Whether you're keeping the kitchen humming or planning your next location,
            we'll help you find funding that actually fits the way restaurants run.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Benefit
              icon={<Utensils className="h-5 w-5 text-[#D4AF37]" />}
              title="Keep inventory stocked"
              body="Cover food and beverage costs even before busy weekends or events."
            />
            <Benefit
              icon={<ChefHat className="h-5 w-5 text-[#D4AF37]" />}
              title="Upgrade kitchen equipment"
              body="Replace ovens, fryers, or POS gear without draining cash flow."
            />
            <Benefit
              icon={<Calendar className="h-5 w-5 text-[#D4AF37]" />}
              title="Handle slow seasons"
              body="Bridge payroll, rent, and utilities through the slower months."
            />
            <Benefit
              icon={<Building2 className="h-5 w-5 text-[#D4AF37]" />}
              title="Expand or renovate"
              body="Open another location, refresh the dining room, or build a patio."
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative border-t border-white/5 bg-[#080808] px-5 py-20 md:px-12 md:py-28">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
                How it works
              </p>
              <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
                Three steps. About 60 seconds.
              </h2>
            </div>
            <Button
              onClick={scrollToForm}
              data-testid="how-cta-button"
              variant="outline"
              className="rounded-lg border-[#D4AF37]/30 bg-transparent px-6 py-3 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F0C94F]"
            >
              Start Now →
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <Step
              num="01"
              title="Enter your info"
              body="Share a few quick details about your restaurant in under a minute."
            />
            <Step
              num="02"
              title="We match you with potential funding options"
              body="We line up programs from our partner network that fit your business."
            />
            <Step
              num="03"
              title="A specialist contacts you quickly"
              body="A funding specialist may reach out within 1 business day to walk through next steps."
            />
          </div>
        </div>
      </section>

      {/* APPLY / FORM */}
      <section
        id="apply"
        data-testid="apply-section"
        className="relative border-t border-white/5 px-5 py-20 md:px-12 md:py-28"
      >
        <div aria-hidden className="absolute inset-x-0 top-0 h-px section-divider" />
        <div className="mx-auto max-w-3xl">
          <div className="mb-8 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
              Pre-qualify in 60 seconds
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              See <span className="text-[#D4AF37]">your funding options</span> now
            </h2>
            <p className="mt-3 text-sm text-zinc-400 sm:text-base">
              Quick, secure, and no impact to your credit score.
            </p>
          </div>

          <LeadForm />
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-t border-white/5 bg-[#080808] px-5 py-12 md:px-12">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4 text-zinc-400">
          <Trust icon={<ShieldCheck className="h-4 w-4 text-[#D4AF37]" />} label="256-bit encrypted" />
          <Trust icon={<CheckCircle2 className="h-4 w-4 text-[#D4AF37]" />} label="No upfront fees" />
          <Trust icon={<Building2 className="h-4 w-4 text-[#D4AF37]" />} label="Restaurant-friendly partners" />
          <Trust icon={<Clock className="h-4 w-4 text-[#D4AF37]" />} label="Fast partner review" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Benefit({ icon, title, body }) {
  return (
    <div
      data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group rounded-2xl border border-white/5 bg-[#0F0F0F] p-7 transition-all hover:-translate-y-1 hover:border-[#D4AF37]/40"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5">
        {icon}
      </div>
      <h3 className="mt-5 text-xl font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{body}</p>
    </div>
  );
}

function Step({ num, title, body }) {
  return (
    <div className="relative rounded-2xl border border-white/5 bg-[#0F0F0F] p-7">
      <div className="text-5xl font-bold tracking-tighter text-[#D4AF37]/20">
        {num}
      </div>
      <h3 className="mt-3 text-xl font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{body}</p>
    </div>
  );
}

function Stat({ title, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-lg font-bold leading-tight text-[#D4AF37] sm:text-xl">
        {title}
      </div>
      <div className="mt-1 text-sm leading-relaxed text-zinc-500 sm:text-[11px]">
        {sub}
      </div>
    </div>
  );
}

function Trust({ icon, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <span>{label}</span>
    </div>
  );
}

// ---------- Rotating restaurant-owner scenarios (illustrative, not real customers) ----------
function ScenarioCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = SCENARIOS.length;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, 5000);
    return () => clearInterval(id);
  }, [paused, total]);

  const go = (delta) => setIdx((i) => (i + delta + total) % total);
  const s = SCENARIOS[idx];
  const initial = s.name.charAt(0);

  return (
    <div
      data-testid="scenario-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="fade-up delay-2 relative"
    >
      <div aria-hidden className="absolute -inset-4 rounded-3xl bg-[#D4AF37]/10 blur-2xl" />
      <div className="relative rounded-2xl border border-white/10 bg-[#0F0F0F]/80 p-6 backdrop-blur-2xl">
        <div className="mb-3 flex items-center">
          <span
            data-testid="scenario-section-label"
            className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37]"
          >
            Restaurant Owner Scenarios
          </span>
        </div>

        <div
          key={idx}
          data-testid={`scenario-card-${idx}`}
          className="fade-up min-h-[190px] sm:min-h-[170px]"
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-lg font-bold text-[#D4AF37]"
              aria-hidden
            >
              {initial}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white">
                {s.name}, {s.role}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300">
            {s.scenario}
          </p>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous scenario"
            data-testid="scenario-prev"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-zinc-400 transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2" role="tablist" aria-label="Scenarios">
            {SCENARIOS.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === idx}
                aria-label={`Show scenario ${i + 1}`}
                data-testid={`scenario-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next scenario"
            data-testid="scenario-next"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-zinc-400 transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
